import React, {useState, useRef, useEffect, useCallback} from "react";
import {flushSync} from "react-dom";
import {
  Button,
  Caption1,
  Textarea,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {Send24Regular, RecordStop24Regular} from "@fluentui/react-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {useAppState} from "../context/AppContext";
import {runChatTurn} from "../../services/chatTurn";
import type {TurnPhase, TurnProgress} from "../../services/chatTurn";
import {REQUEST_TIMEOUT_MS} from "../../services/librechatApi";
import {splitStreamingText} from "../../services/actionParser";
import {OUTCOME_MARKERS} from "../../services/actionExecutor";
import type {ActionOutcome} from "../../services/actionExecutor";
import type {StoredMessage} from "../../services/chatStore";
import {
  logUserMessage,
  logSentToApi,
  logApiResponse,
  logActionResults,
  logError,
} from "../../services/debugLogger";
import {
  errorReference,
  recordSendBlockedUnconfiguredEvent,
  recordTurnAbandonedEvent,
} from "../../services/telemetry";

/** What the pending bubble says while the user waits. */
const PHASE_LABELS: Record<TurnPhase, string> = {
  "reading-workbook": "Reading your spreadsheet…",
  thinking: "Thinking…",
  "running-actions": "Applying changes…",
  reviewing: "Reviewing the results…",
};

/** How often the waiting bubble's clock ticks. */
const ELAPSED_TICK_MS = 1000;

/**
 * When to start warning that the request is heading for its own timeout,
 * rather than letting it fail at the ceiling with no notice.
 */
const SLOW_TURN_MS = REQUEST_TIMEOUT_MS * 0.6;

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

const EXAMPLE_PROMPTS = [
  "Summarise the selected data",
  "Create a bar chart from this data",
  "Add a total row with formulas",
];

/**
 * Opens the bubble the assistant streams into, unless one is already waiting.
 * It is opened on send rather than on the first iteration so the workbook-
 * reading phase — the slowest part of a turn — has somewhere to report itself.
 */
function openAssistantMessage(messages: StoredMessage[]): StoredMessage[] {
  const last = messages[messages.length - 1];
  if (last?.role === "assistant" && last.content === "" && !last.actionBlocks) {
    return messages;
  }
  return [...messages, {role: "assistant", content: ""}];
}

/**
 * Swaps the streamed-in text of the open assistant bubble for the cleaned-up
 * text and attaches any action JSON (shown collapsed). The bubble is dropped
 * entirely only when there's neither prose nor action markup left to show.
 */
function closeAssistantMessage(
  messages: StoredMessage[],
  content: string,
  actionBlocks: string[],
): StoredMessage[] {
  const last = messages[messages.length - 1];
  if (last?.role !== "assistant") return messages;
  if (!content.trim() && actionBlocks.length === 0)
    return messages.slice(0, -1);
  return [...messages.slice(0, -1), {...last, content, actionBlocks}];
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  messages: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  userMsg: {
    alignSelf: "flex-end",
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: "8px 8px 2px 8px",
    padding: "8px 12px",
    maxWidth: "85%",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    fontSize: "13px",
  },
  assistantMsg: {
    alignSelf: "flex-start",
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    borderRadius: "8px 8px 8px 2px",
    padding: "8px 12px",
    maxWidth: "85%",
    wordBreak: "break-word",
    fontSize: "13px",
    "& p": {margin: "0 0 8px 0"},
    "& p:last-child": {marginBottom: 0},
    "& pre": {
      backgroundColor: tokens.colorNeutralBackground1,
      padding: "8px",
      borderRadius: "8px",
      overflowX: "auto",
      fontSize: "12px",
    },
    "& code": {
      fontSize: "12px",
      fontFamily: "monospace",
    },
    "& ul, & ol": {margin: "4px 0", paddingLeft: "20px"},
    "& table": {
      borderCollapse: "collapse",
      fontSize: "12px",
      margin: "4px 0",
    },
    "& th, & td": {
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      padding: "4px 8px",
    },
  } as any,
  pendingActions: {
    display: "block",
    marginTop: "2px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  actionBlocks: {
    marginTop: "6px",
    fontSize: "12px",
    "& summary": {
      cursor: "pointer",
      color: tokens.colorNeutralForeground3,
    },
    "& pre": {
      backgroundColor: tokens.colorNeutralBackground1,
      padding: "8px",
      borderRadius: "8px",
      overflowX: "auto",
      fontSize: "11px",
      marginTop: "4px",
    },
  } as any,
  systemMsg: {
    alignSelf: "center",
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
    padding: "4px 8px",
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "16px",
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  examples: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    width: "100%",
    marginTop: "4px",
  },
  phase: {
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  slowNotice: {
    display: "block",
    marginTop: "4px",
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  actionResult: {
    alignSelf: "stretch",
    fontSize: "12px",
    padding: "6px 10px",
    borderRadius: "8px",
    wordBreak: "break-word",
  },
  actionOk: {
    color: tokens.colorPaletteGreenForeground2,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  actionFailed: {
    color: tokens.colorPaletteRedForeground2,
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
  },
  // Deliberately not the failure red: the user turned this down on purpose,
  // and dressing their own choice as an error is what issue #47 is about.
  actionDeclined: {
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Single rounded container: the textarea's own frame is hidden so only this
  // outer card is visible — no box-inside-a-box.
  inputArea: {
    display: "flex",
    alignItems: "flex-end",
    gap: "4px",
    margin: "8px 12px 12px",
    padding: "8px 6px 8px 14px",
    borderRadius: "16px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    ":focus-within": {
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  textarea: {
    flex: 1,
    minWidth: 0,
    alignSelf: "stretch",
    // This class lands on the Fluent root (fui-Textarea), so target it with
    // "&" — not as a descendant. The border must be killed in EVERY state:
    // base, hover, and focus-within, because Fluent's outlineInteractive
    // re-applies a full border on focus.
    "&, &.fui-Textarea": {
      border: "none",
      backgroundColor: "transparent",
      minHeight: 0,
      paddingBottom: 0,
    },
    "&:hover, &:focus-within, &:active": {
      border: "none",
      backgroundColor: "transparent",
    },
    "&::after, &:focus-within::after": {
      display: "none",
      border: "none",
      boxShadow: "none",
      content: "none",
    },
    "& .fui-Textarea__textarea": {
      border: "none",
      outline: "none",
      backgroundColor: "transparent",
      boxShadow: "none",
      minHeight: 0,
      padding: "2px 0",
      lineHeight: "20px",
    },
  } as any,
  sendButton: {
    borderRadius: "50%",
    minWidth: "32px",
    flexShrink: 0,
    alignSelf: "flex-end",
  },
});

export function ChatPanel() {
  const styles = useStyles();
  const {
    apiKey,
    agentId,
    maxIterations,
    setView,
    view,
    messages,
    setMessages,
    historyRef,
    commitActiveThread,
  } = useAppState();

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<TurnPhase | null>(null);
  const [progress, setProgress] = useState<TurnProgress | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Holds the pending script confirmation's resolver — window.confirm isn't
  // supported in the Excel task pane, so this drives a real dialog instead.
  const confirmResolveRef = useRef<((proceed: boolean) => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null);

  const confirmAction = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmMessage(message);
    });
  }, []);

  const resolveConfirm = useCallback((proceed: boolean) => {
    confirmResolveRef.current?.(proceed);
    confirmResolveRef.current = null;
    setConfirmMessage(null);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
  }, [messages]);

  // Grows the box with its contents instead of leaving a fixed two-row well.
  useEffect(() => {
    if (view !== "chat") return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [input, view]);

  // Mirrors elapsedMs into a ref so handleStop — a stable callback with an
  // empty dependency list — can read the current wait rather than the one
  // in scope when it was created.
  const elapsedRef = useRef(0);

  // A turn can run for minutes, so the bubble carries a clock: without it a
  // long wait is indistinguishable from a hung one.
  useEffect(() => {
    if (!loading) return;
    const startedAt = Date.now();
    setElapsedMs(0);
    elapsedRef.current = 0;
    const tick = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setElapsedMs(elapsed);
      elapsedRef.current = elapsed;
    }, ELAPSED_TICK_MS);
    return () => clearInterval(tick);
  }, [loading]);

  const handleStop = useCallback(() => {
    // The empirical answer to "how long will people wait before giving up"
    // (issue #58) — recorded here, not inside runChatTurn, because the loop
    // reports every abort as `stopReason: "stopped"` regardless of how long
    // the wait was.
    recordTurnAbandonedEvent(elapsedRef.current);
    abortRef.current?.abort();
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    if (!apiKey || !agentId) {
      recordSendBlockedUnconfiguredEvent();
      setView("settings");
      return;
    }

    setInput("");
    setMessages((prev) =>
      openAssistantMessage([...prev, {role: "user", content: text}]),
    );
    setLoading(true);
    setPhase("reading-workbook");
    // Persists the question straight away, so a turn that fails or is
    // interrupted still leaves it on screen. The model's history is committed
    // only once the turn succeeds — a failed exchange is deliberately left out
    // of what the model is told next, as it always has been.
    commitActiveThread();

    logUserMessage(text);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const {
        fullResponse,
        actionResults,
        stopNotice,
        messages: history,
        turnId,
      } = await runChatTurn(
        {
          agentId,
          apiKey,
          userMessage: text,
          maxIterations,
          history: historyRef.current,
          signal: controller.signal,
          confirm: confirmAction,
        },
        {
          onPromptBuilt: (sentMessages) => {
            logSentToApi({agentId, messages: sentMessages, stream: true});
          },
          onPhase: (nextPhase, nextProgress) => {
            setPhase(nextPhase);
            setProgress(nextProgress);
          },
          onIterationStart: () => {
            setMessages(openAssistantMessage);
          },
          onChunk: (chunk) => {
            // flushSync forces React to render synchronously so the user
            // sees each token as it arrives (no batching delay).
            flushSync(() => {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    content: last.content + chunk,
                  };
                }
                return updated;
              });
            });
          },
          onIterationEnd: (displayText, rawActionBlocks) => {
            setMessages((prev) =>
              closeAssistantMessage(prev, displayText, rawActionBlocks),
            );
          },
          onActionResult: (result) => {
            setMessages((prev) => [
              ...prev,
              {
                role: "system",
                content: `${OUTCOME_MARKERS[result.outcome]} ${result.message}`,
                outcome: result.outcome,
              },
            ]);
          },
        },
      );

      logApiResponse(fullResponse);

      historyRef.current = history;

      if (stopNotice) {
        // A code the user can quote to support instead of describing what happened.
        const content = `${stopNotice} (Error ref: ${errorReference(turnId)})`;
        setMessages((prev) => [...prev, {role: "system", content}]);
      }

      if (actionResults.length > 0) {
        logActionResults(actionResults);
      }
    } catch (error) {
      logError("ChatPanel.handleSend", error);
      const msg =
        error instanceof Error ? error.message : "Something went wrong";
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.role === "assistant" && last.content === "") {
          // Replace empty assistant message with error
          updated[updated.length - 1] = {
            role: "system",
            content: `Error: ${msg}`,
          };
        } else {
          updated.push({role: "system", content: `Error: ${msg}`});
        }
        return updated;
      });
    } finally {
      abortRef.current = null;
      setLoading(false);
      setPhase(null);
      setProgress(null);
      commitActiveThread();
    }
  }, [
    input,
    loading,
    apiKey,
    agentId,
    maxIterations,
    setView,
    setMessages,
    historyRef,
    commitActiveThread,
    confirmAction,
  ]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function classFor(msg: StoredMessage): string {
    if (msg.role === "user") return styles.userMsg;
    if (msg.role === "assistant") return styles.assistantMsg;
    if (msg.outcome) {
      const outcomeStyles: Record<ActionOutcome, string> = {
        success: styles.actionOk,
        failure: styles.actionFailed,
        declined: styles.actionDeclined,
      };
      return mergeClasses(styles.actionResult, outcomeStyles[msg.outcome]);
    }
    return styles.systemMsg;
  }

  /**
   * What an assistant bubble should show right now.
   *
   * A finished bubble is already clean: `parseActions` has pulled the action
   * JSON out into `actionBlocks`, shown collapsed. A streaming one is raw, so
   * the same JSON would otherwise be typed out character by character in front
   * of the user. Splitting it here keeps the prose streaming — the part worth
   * watching — while the block it is about to emit shows up as one quiet line.
   */
  function assistantView(
    msg: StoredMessage,
    index: number,
  ): {text: string; pendingActions: number} {
    const streaming =
      msg.role === "assistant" &&
      loading &&
      index === messages.length - 1 &&
      !msg.actionBlocks;
    if (!streaming) return {text: msg.content, pendingActions: 0};
    const {visibleText, actionBlockCount} = splitStreamingText(msg.content);
    return {text: visibleText, pendingActions: actionBlockCount};
  }

  /** The waiting bubble reports what the turn is doing, not just that it is busy. */
  function pendingLabel(index: number): React.ReactNode {
    if (!loading || index !== messages.length - 1) return null;
    // Step 0 is the workbook read that happens before any request goes out.
    const step =
      progress && progress.iteration > 0
        ? `Step ${progress.iteration} of ${progress.of} · `
        : "";
    const slow = elapsedMs >= SLOW_TURN_MS;
    return (
      <>
        <Caption1 className={styles.phase}>
          {step}
          {phase ? PHASE_LABELS[phase] : "Starting…"} {formatElapsed(elapsedMs)}
        </Caption1>
        {slow && (
          <Caption1 className={styles.slowNotice}>
            Still going — large workbooks take a while. This request gives up
            after {Math.round(REQUEST_TIMEOUT_MS / 60000)} minutes.
          </Caption1>
        )}
      </>
    );
  }

  return (
    <div className={styles.container}>
      {messages.length === 0 ? (
        <div className={styles.emptyState}>
          <Caption1>
            Select some data and ask a question, or pick an example.
          </Caption1>
          <div className={styles.examples}>
            {EXAMPLE_PROMPTS.map((prompt) => (
              <Button
                key={prompt}
                appearance="outline"
                size="small"
                onClick={() => {
                  setInput(prompt);
                  textareaRef.current?.focus();
                }}
              >
                {prompt}
              </Button>
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.messages}>
          {messages.map((msg, i) => {
            const {text, pendingActions} = assistantView(msg, i);
            return (
              <div key={i} className={classFor(msg)}>
                {msg.role === "assistant" ? (
                  <>
                    {text ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {text}
                      </ReactMarkdown>
                    ) : pendingActions === 0 ? (
                      pendingLabel(i)
                    ) : null}
                    {pendingActions > 0 && (
                      <Caption1 className={styles.pendingActions}>
                        {pendingActions === 1
                          ? "A preparar instrução…"
                          : `A preparar ${pendingActions} instruções…`}
                      </Caption1>
                    )}
                    {msg.actionBlocks && msg.actionBlocks.length > 0 && (
                      <details className={styles.actionBlocks}>
                        <summary>
                          {msg.actionBlocks.length === 1
                            ? "Instrução executada"
                            : `${msg.actionBlocks.length} instruções executadas`}
                        </summary>
                        {msg.actionBlocks.map((block, j) => (
                          <pre key={j}>{block}</pre>
                        ))}
                      </details>
                    )}
                  </>
                ) : (
                  msg.content || pendingLabel(i)
                )}
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}

      <div className={styles.inputArea}>
        <Textarea
          className={styles.textarea}
          textarea={{ref: textareaRef}}
          value={input}
          onChange={(_, data) => setInput(data.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your data..."
          resize="none"
          rows={1}
          appearance="filled-lighter"
        />
        {loading ? (
          <Button
            className={styles.sendButton}
            appearance="subtle"
            shape="circular"
            icon={<RecordStop24Regular />}
            onClick={handleStop}
            aria-label="Stop"
            title="Stop"
          />
        ) : (
          <Button
            className={styles.sendButton}
            appearance="subtle"
            shape="circular"
            icon={<Send24Regular />}
            onClick={handleSend}
            disabled={!input.trim()}
            aria-label="Send"
            title="Send"
          />
        )}
      </div>

      <Dialog open={confirmMessage !== null}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirmar ação</DialogTitle>
            <DialogContent>{confirmMessage}</DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => resolveConfirm(false)}
              >
                Cancelar
              </Button>
              <Button appearance="primary" onClick={() => resolveConfirm(true)}>
                Continuar
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
